import { CLOUD_BASE_URL } from '../Component/commonComponents/ApiEndpoints'

const DEFAULT_TITLE = 'Vibhum EduSys | Smart School Management Portal'

/** Public folder placeholder when API provides no logo */
export const NO_IMAGE_SRC = `${process.env.PUBLIC_URL || ''}/logo.jpg`

/** Public folder placeholder when API provides no institute stamp */
export const NO_STAMP_SRC = `${process.env.PUBLIC_URL || ''}/no_stamp.png`

/**
 * Full URL for displaying an institute logo in <img src>. Falls back to no_image.png.
 */
export function resolveInstituteLogoSrc(logoUrl) {
  if (!logoUrl || logoUrl === 'no_image.png') return NO_IMAGE_SRC
  const raw = String(logoUrl).trim()
  if (!raw || raw === 'null' || raw === 'undefined') return NO_IMAGE_SRC
  if (/^https?:\/\//i.test(raw)) {
    return raw.replace(/^http:\/\//i, 'https://')
  }
  const base = CLOUD_BASE_URL.replace(/\/$/, '')
  return `${base}/${raw.replace(/^\//, '')}`
}

/**
 * Stamp image URL for <img src>. Full URL or path under upload host; null/empty → no_stamp.png.
 */
export function resolveInstituteStampSrc(stampUrl) {
  if (stampUrl == null || stampUrl === 'null' || stampUrl === 'undefined') {
    return NO_STAMP_SRC
  }
  const raw = String(stampUrl).trim()
  if (!raw || raw === 'null' || raw === 'undefined') return NO_STAMP_SRC
  if (/^https?:\/\//i.test(raw)) {
    return raw.replace(/^http:\/\//i, 'https://')
  }
  const base = CLOUD_BASE_URL.replace(/\/$/, '')
  return `${base}/${raw.replace(/^\//, '')}`
}

/** Raw stamp path from API institute object (field names and nesting vary by endpoint). */
export function getInstituteStampPathFromRecord(institute) {
  if (!institute || typeof institute !== 'object') return ''
  const nested =
    institute.institute && typeof institute.institute === 'object'
      ? institute.institute
      : null
  const candidates = [
    institute.institute_stamp,
    institute.stamp_url_img,
    nested?.institute_stamp,
    nested?.stamp_url_img,
  ]
  for (const v of candidates) {
    if (v == null) continue
    const s = String(v).trim()
    if (s && s !== 'null' && s !== 'undefined') return s
  }
  return ''
}

/** Raw logo path from API institute object (field names and nesting vary by endpoint). */
export function getInstituteLogoPathFromRecord(institute) {
  if (!institute || typeof institute !== 'object') return ''
  const nested =
    institute.institute && typeof institute.institute === 'object'
      ? institute.institute
      : null
  const candidates = [
    institute.institute_logo,
    institute.logo,
    institute.url,
    institute.image,
    institute.logo_url,
    institute.photo,
    institute.institute_logo_url,
    nested?.institute_logo,
    nested?.logo,
    nested?.url,
    nested?.image,
  ]
  for (const v of candidates) {
    if (v == null) continue
    const s = String(v).trim()
    if (s && s !== 'null' && s !== 'undefined') return s
  }
  return ''
}

/** <img src> for an institute row from the API, or placeholder when missing. */
export function resolveInstituteLogoFromRecord(institute) {
  return resolveInstituteLogoSrc(getInstituteLogoPathFromRecord(institute))
}

/**
 * Flatten `/users/institute` pivot rows and align id/name/logo fields.
 */
export function normalizeInstituteRow(inst) {
  if (!inst || typeof inst !== 'object') return inst
  const nested =
    inst.institute && typeof inst.institute === 'object' ? inst.institute : null
  const merged = nested ? { ...nested, ...inst } : { ...inst }
  const id =
    merged.institute_id ??
    merged.id ??
    nested?.institute_id ??
    nested?.id
  const name =
    merged.institute_name ??
    merged.name ??
    nested?.institute_name ??
    nested?.name ??
    ''
  const logoPath = getInstituteLogoPathFromRecord(inst)
  return {
    ...merged,
    institute_id: id,
    institute_name: name,
    ...(logoPath ? { institute_logo: logoPath } : {}),
  }
}

export function normalizeInstituteList(list) {
  if (!Array.isArray(list)) return []
  return list.map(normalizeInstituteRow)
}

/** Axios response → institute array (handles different envelope shapes). */
export function extractInstituteListFromResponse(res) {
  const d = res?.data
  if (!d) return []
  if (Array.isArray(d)) return d
  if (Array.isArray(d.data)) return d.data
  if (Array.isArray(d.institutes)) return d.institutes
  if (d.data && Array.isArray(d.data.institutes)) return d.data.institutes
  return []
}

/**
 * Merge assigned institutes with `/institute/all` so logos appear when the assigned
 * endpoint omits `institute_logo` (common for pivot-only responses).
 */
export function mergeInstituteRowsWithFullList(assignedList, fullList) {
  const normalizedAssigned = normalizeInstituteList(assignedList)
  const normalizedFull = normalizeInstituteList(fullList)
  const byId = new Map(
    normalizedFull.map((row) => {
      const id = String(row.institute_id ?? row.id ?? '')
      return [id, row]
    }),
  )
  return normalizedAssigned.map((row) => {
    const id = String(row.institute_id ?? row.id ?? '')
    const full = byId.get(id)
    if (!full) return row
    const logoFromAssigned = getInstituteLogoPathFromRecord(row)
    const logoFromFull = getInstituteLogoPathFromRecord(full)
    const logoPath = logoFromAssigned || logoFromFull
    return {
      ...full,
      ...row,
      institute_id: row.institute_id ?? full.institute_id,
      institute_name: row.institute_name || full.institute_name,
      ...(logoPath ? { institute_logo: logoPath } : {}),
    }
  })
}

/**
 * Resolve favicon URL from cookie/API value (full URL or path under upload host).
 */
export function resolveInstituteFaviconHref(logoUrl) {
  if (!logoUrl || logoUrl === 'no_image.png') return NO_IMAGE_SRC
  const raw = String(logoUrl).trim()
  if (/^https?:\/\//i.test(raw)) return raw
  const base = CLOUD_BASE_URL.replace(/\/$/, '')
  return `${base}/${raw.replace(/^\//, '')}`
}

/**
 * Update document title and <link rel="icon"> from institute name + logo.
 */
export function applyInstituteBranding(name, logoUrl) {
  if (typeof document === 'undefined') return

  const title =
    name && String(name).trim() ? String(name).trim() : DEFAULT_TITLE
  document.title = title

  const href = resolveInstituteFaviconHref(logoUrl)
  let link = document.querySelector('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = href
  if (/\.jpe?g$/i.test(href)) {
    link.type = 'image/jpeg'
  } else if (/\.png$/i.test(href)) {
    link.type = 'image/png'
  } else {
    link.removeAttribute('type')
  }
}
